import React, { useState, ReactNode, useEffect } from "react";
import { EFootprint } from "../../../../Model/PlateType";
import CheckBox from "../../../Buttons/CheckBox";
import {
  SlotAddress,
  SlotCorrectionResult,
} from "../../../../Model/SlotCorrection";
import { StorageConfig, HotelConfig } from "../../../../Model/StorageConfig";
import { EModuleType } from "../../../../Model/EModuleType";
import { getModulesByType } from "../../../../redux/reducers/ModuleReducer";
import { getStorageConfig } from "../../../../redux/reducers/StorageConfigReducer";
import { useTypedSelector } from "../../../../redux/reducers/RootReducer";
import "./SlotCorrection.css";
import {
  getPlateAddresses,
  getPlateAddressById,
} from "../../../../redux/reducers/PlateAddressReducer";
import { EPlateAddressState } from "../../../../Model/EPlateAddressState";
import { PlateAddress } from "../../../../Model/PlateAddress";

const getHotelFootPrint = (
  hotelNumber: number,
  storageConfig: StorageConfig
) => {
  let hotels = storageConfig.hotels.filter(
    (hotel) => hotel.number == hotelNumber
  );
  if (hotels.length != 0) {
    return hotels[0].footprint;
  }

  return EFootprint.Unknown;
};

const isSameSlotAddress = (
  slotAddress1: SlotAddress,
  slotAddress2: SlotAddress
): boolean => {
  return (
    slotAddress1.hotelNumber == slotAddress2.hotelNumber &&
    slotAddress1.moduleIdentifier == slotAddress2.moduleIdentifier &&
    slotAddress1.slotNumber == slotAddress2.slotNumber
  );
};

type Prop = {
  setSlotAddress: (value: SlotAddress[]) => void;
  slotAddresses: SlotAddress[];
  moduleIdentifier: string;
  slotCorrectionResults: SlotCorrectionResult[];
};

export default function FailedSlotContent({
  setSlotAddress,
  slotAddresses,
  moduleIdentifier,
  slotCorrectionResults,
}: Prop) {
  const storageConfig = useTypedSelector((state) =>
    getStorageConfig(state, moduleIdentifier)
  );

  const plateAddressById = useTypedSelector((state) =>
    getPlateAddressById(state)
  );

  const [failedPlateAddresses, setFailedPlateAddresses] = useState(
    [] as PlateAddress[]
  );

  const slotAddressPerHotel: Map<string, SlotAddress[]> = new Map();
  failedPlateAddresses.forEach((pa) => {
    const hotelKey = `${
      EFootprint[getHotelFootPrint(pa.hotelNo, storageConfig)]
    } ${pa.hotelNo}`;
    if (slotAddressPerHotel.has(hotelKey)) {
      let slotAddresses = slotAddressPerHotel.get(hotelKey);
      slotAddresses?.push({
        moduleIdentifier: pa.moduleIdentifier,
        hotelNumber: pa.hotelNo,
        slotNumber: pa.slotNo,
      });
    } else {
      slotAddressPerHotel.set(hotelKey, [
        {
          moduleIdentifier: pa.moduleIdentifier,
          hotelNumber: pa.hotelNo,
          slotNumber: pa.slotNo,
        },
      ]);
    }
  });

  const handleSelectAllSlot = () => {
    if (slotAddresses.length === failedPlateAddresses.length) {
      setSlotAddress([]);
    } else {
      setSlotAddress(
        failedPlateAddresses.map((pa) => {
          return {
            moduleIdentifier: pa.moduleIdentifier,
            hotelNumber: pa.hotelNo,
            slotNumber: pa.slotNo,
          };
        })
      );
    }
  };

  const handleSelectSlot = (slotAddress: SlotAddress) => {
    let index = slotAddresses.findIndex((sa) =>
      isSameSlotAddress(sa, slotAddress)
    );
    if (index == -1) {
      setSlotAddress([...slotAddresses, slotAddress]);
    } else {
      let newSlotAddresses = [...slotAddresses];
      newSlotAddresses.splice(index, 1);
      setSlotAddress(newSlotAddresses);
    }
  };

  let failedCheckboxes: ReactNode[] = [];

  slotAddressPerHotel.forEach((value, key) => {
    failedCheckboxes.push(
      <>
        <div className="slot-hotel-header">{key}</div>
        <div className="slot-select">
          {value.map((item, index) => (
            <div className="slot-select-item">
              <CheckBox
                checked={
                  slotAddresses.findIndex((sa) =>
                    isSameSlotAddress(sa, item)
                  ) !== -1
                }
                id={`failed-slot${index + 1}-slot-correction`}
                name={`failed-slot${index + 1}-slot-correction`}
                onChange={() => handleSelectSlot(item)}
                colorVariant="alternate"
                size="small"
              />
              <label>{`Slot ${item.slotNumber}`}</label>
            </div>
          ))}
        </div>
      </>
    );
  });

  useEffect(() => {
    let newfailedPlates = [] as PlateAddress[];
    for (const plateId in plateAddressById) {
      const plateAddress = plateAddressById[plateId];
      const slotCorrectionResult = slotCorrectionResults.find(
        (sc) =>
          sc.moduleIdentifier === plateAddress.moduleIdentifier &&
          sc.hotelNumber === plateAddress.hotelNo &&
          sc.slotNumber == plateAddress.slotNo
      );
      if (
        plateAddress.plateAddressState === EPlateAddressState.Faulty &&
        plateAddress.moduleIdentifier === moduleIdentifier &&
        slotCorrectionResult === undefined
      ) {
        newfailedPlates.push(plateAddress);
      }
    }
    newfailedPlates.sort((a, b) => {
      if (a.hotelNo < b.hotelNo) {
        return -1;
      }

      if (a.hotelNo === b.hotelNo && a.slotNo < b.slotNo) {
        return -1;
      }

      return 1;
    });
    setFailedPlateAddresses(newfailedPlates);
  }, [plateAddressById, moduleIdentifier, slotCorrectionResults]);

  return (
    <>
      {failedPlateAddresses.length == 0 && (
        <div
          style={{
            fontFamily: "Roboto",
            fontSize: "14px",
            fontStyle: "italic",
            fontWeight: "400",
            color: "#5D5D5D",
          }}
        >
          No failed plate found
        </div>
      )}
      {failedPlateAddresses.length > 0 && (
        <>
          <div className="slot-select-item" style={{ padding: "8px 0 0 12px" }}>
            <CheckBox
              checked={slotAddresses.length === failedPlateAddresses.length}
              id={`hotels-slot-correction`}
              name={`hotels-slot-correction`}
              onChange={() => handleSelectAllSlot()}
              colorVariant="alternate"
              size="small"
            />
            <label>Select All</label>
          </div>
          {failedCheckboxes}
        </>
      )}
    </>
  );
}
